import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Input, Checkbox } from 'antd';
import { Divider, notification } from 'syngenta-digital-cropwise-react-ui-kit';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { LoadingView } from '../../Interstitial';
import search from 'assets/search.svg';
import { SETTINGS_ACTION, WizardStepProps } from 'models';
import PartnerModule from 'models/PartnerModule';
import { WizardStepSubTitle } from '../subviews/WizardStepSubTitle';
import MasonryComponent from './MasonryComponent';
import NoDataToDisplay from './NoDataToDisplay';
import { useTranslation } from 'react-i18next';

const CheckboxGroup = Checkbox.Group;

type ListResponse = {
  items: any[];
  wizardData?: any;
};

const GenericWizardStep = ({
  integration,
  accessToken,
  step,
  wizardData,
  setDisableNextBtn,
  setDisablePrevBtn,
  prevBtnClicked,
}: WizardStepProps) => {
  const [items, setItems] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<CheckboxValueType[]>([]);
  const [importFutureItems, setImportFutureItems] = useState(false);
  const [itemsIndeterminate, setItemsIndeterminate] = useState(false);
  const [checkAllItems, setCheckAllItems] = useState(false);
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [computedCSSClasses, setComputedCSSClasses] = useState<{
    selectAllCSSClass?: string;
    importFutureItemsCSSClass?: string;
    gridColCSS?: string;
  }>({});
  const [forceUpdate, setForceUpdate] = useState(0);
  const partnerId = integration?.partner_id;
  const integrationRef = useRef(integration);
  const wizardDataRef = useRef(wizardData);
  const stepRef = useRef(step);
  const itemsRef = useRef<any[]>(items);
  const prevBtnClickedRef = useRef(prevBtnClicked);
  const importFutureItemsRef = useRef(importFutureItems);
  const _masonryRef = useRef<any>();
  const listContainerRef = useRef<any>();
  const searchTextRef = useRef(searchText);
  const filteredListRef = useRef(filteredList);

  useEffect(() => {
    setDisablePrevBtn(isLoading);
  }, [isLoading, setDisablePrevBtn]);

  useEffect(() => {
    prevBtnClickedRef.current = prevBtnClicked;
  }, [prevBtnClicked]);

  useEffect(() => {
    stepRef.current = step;
    itemsRef.current = [];
    setFilteredList([]);
    if (wizardDataRef.current?.selectedItems?.length) {
      wizardDataRef.current.selectedItems = [];
    }
    setComputedCSSClasses({
      selectAllCSSClass: step.importFutureItems
        ? 'mr-24'
        : 'select-all w-100% flex flex-row-reverse items-stretch px-14',
      importFutureItemsCSSClass: step.importFutureItems ? ' px-5' : '',
      gridColCSS: step.gridColSize === 1 ? ' py-12' : ' py-24',
    });
  }, [step]);

  const getInitialSelection = (_items: any[]) =>
    stepRef.current.selectAll ? _items.map((_item: any) => _item.id) : [];

  const getPreviousItems = (_items: any[]) => {
    let _selectedItems: any[] = getInitialSelection(_items);
    if (partnerId) {
      let preSelectedList: any[] = PartnerModule.get_settings_values({
        partnerId,
        accessToken,
        settings: integrationRef.current?.settings,
        wizardData: wizardDataRef.current,
        step: stepRef.current,
      });

      if (!preSelectedList) {
        return _selectedItems;
      }

      if (preSelectedList.length === 0) {
        /**
         * If the property saved in settings as empty
         * then it was deleted because if the import future items flag
         */
        importFutureItemsRef.current = true;
        preSelectedList = getInitialSelection(_items);
      }
      _selectedItems = [
        ...preSelectedList.filter((_selectedItem: string) =>
          _items.find((_item: any) => _item.id === _selectedItem),
        ),
      ];
    }
    return _selectedItems;
  };
  const updateWizardData = (response: ListResponse) => {
    if (response?.wizardData) {
      wizardDataRef.current = {
        ...wizardDataRef.current,
        ...response.wizardData,
      };
    }
  };
  useEffect(() => {
    if (accessToken && partnerId) {
      setIsLoading(true);
      PartnerModule.get_list_items({
        partnerId,
        accessToken,
        settings: integrationRef.current?.settings,
        wizardData: wizardDataRef.current,
        step: stepRef.current,
      })
        .then((response: ListResponse) => {
          if (!response?.items) return;
          setIsLoading(false);
          updateWizardData(response);
          itemsRef.current = response.items;
          setItems([...response.items]);
          setSelectedItems(getPreviousItems(response.items));
        })
        .catch((error: any) => {
          console.error(error);
          setIsLoading(false);
          notification.error({
            message: 'Integration error',
            description: `Failed to retrieve ${stepRef.current.itemDisplayName}`,
            placement: 'topRight',
          });
        });
    }
  }, [accessToken, partnerId, step]);

  useEffect(() => {
    if (partnerId && selectedItems && itemsRef.current.length && integrationRef.current) {
      wizardDataRef.current.selectedItems = selectedItems;
      setDisableNextBtn(selectedItems.length === 0);
      setCheckAllItems(
        !!selectedItems?.length && itemsRef.current.length === selectedItems?.length,
      );
      setItemsIndeterminate(
        !!selectedItems?.length && selectedItems?.length < itemsRef.current.length,
      );
      if (stepRef.current.importFutureItems && importFutureItemsRef.current) {
        importFutureItemsRef.current = selectedItems.length === itemsRef.current.length;
        setImportFutureItems(selectedItems.length === itemsRef.current.length);
      }
      integrationRef.current.settings = PartnerModule.update_settings({
        partnerId,
        accessToken,
        settings: integrationRef.current.settings,
        wizardData: wizardDataRef.current,
        step: stepRef.current,
        action: importFutureItemsRef.current ? SETTINGS_ACTION.DELETE : SETTINGS_ACTION.UPDATE,
      });
    }
  }, [partnerId, accessToken, selectedItems, setDisableNextBtn]);

  useEffect(() => {
    if (partnerId) {
      const updatedFilteredList = PartnerModule.search_items(
        partnerId,
        stepRef.current.type,
        searchText,
        items,
      );
      filteredListRef.current = updatedFilteredList;
      setFilteredList(updatedFilteredList);
    }
  }, [partnerId, items, searchText]);

  useEffect(() => {
    if (searchTextRef.current !== '') {
      setSearchText('');
    }
  }, [items]);

  useEffect(() => {
    // force masonry re-render when screen size change
    if (filteredListRef.current) {
      setFilteredList([...filteredListRef.current]);
    }
  }, [forceUpdate]);

  const onItemsSelectionChange = (list: CheckboxValueType[]) => {
    /**
     * virtualized list hides list items, so the list object recieved is not the full list
     * but rather the displayed records, hence comparing what is un selected only from the
     * displayed list items
     */
    if (_masonryRef.current) {
      const displayedIds = [filteredList[_masonryRef.current._startIndex].id];
      let counter = _masonryRef.current._startIndex;
      while (counter < _masonryRef.current._stopIndex) {
        counter++;
        displayedIds.push(filteredList[counter].id);
      }
      // Check if any items were removed
      const removedListItems = displayedIds.filter(
        (_item: CheckboxValueType) => !list.includes(_item),
      );
      // remove items
      const updatedList = selectedItems.filter(
        (_item: CheckboxValueType) => !removedListItems.includes(_item),
      );
      // add items
      updatedList.push(
        ...list.filter((_item: CheckboxValueType) => !selectedItems?.includes(_item)),
      );
      setSelectedItems(updatedList);
    }
  };

  const onCheckAllitemsChange = (e: CheckboxChangeEvent) => {
    setSelectedItems(e.target.checked ? [...items.map((_item: any) => _item.id)] : []);
    setCheckAllItems(e.target.checked);
  };

  const onSearch = (e: any) => {
    setSearchText(e.target.value);
    searchTextRef.current = e.target.value;
  };

  const onImportFutureItems = (e: CheckboxChangeEvent) => {
    setImportFutureItems(e.target.checked);
    importFutureItemsRef.current = e.target.checked;
    /**
     * In case importFutureItems is true then selected list
     * should be empty,
     * In case importFutureItems is switched to false then all all items
     */
    if (partnerId && stepRef.current?.importFutureItems && integrationRef.current) {
      integrationRef.current.settings = PartnerModule.update_settings({
        partnerId,
        accessToken,
        settings: integrationRef.current.settings,
        wizardData: wizardDataRef.current,
        step: stepRef.current,
        action: e.target.checked ? SETTINGS_ACTION.DELETE : SETTINGS_ACTION.UPDATE,
      });
    }
  };
  const { t } = useTranslation();

  return (
    <>
      {isLoading ? (
        <LoadingView dontUseFullScreen={true} />
      ) : (
        <div data-cy="wizard-items-comp" className="h-100% flex flex-col items-stretch">
          {step.subTitle && <WizardStepSubTitle subTitle={step.subTitle} />}
          {(!items || items.length === 0) && <NoDataToDisplay />}
          {partnerId && items?.length > 0 && (
            <div className="flex flex-col flex-1 w-55% min-w-250 min-h-0 m-auto">
              <Input
                placeholder="Search"
                value={searchText}
                onChange={onSearch}
                data-cy="input-search"
                className="input__search bg-neutral-10 border-1 border-gray-date-range-border rounded-4 px-5 mb-20 mx-auto"
                prefix={<img alt="search" src={search}></img>}
                allowClear
              />
              <div
                className={`flex items-center flex-wrap ${computedCSSClasses.importFutureItemsCSSClass}`}
              >
                <Checkbox
                  data-cy="select-all"
                  indeterminate={itemsIndeterminate}
                  onChange={onCheckAllitemsChange}
                  checked={checkAllItems}
                  className={computedCSSClasses.selectAllCSSClass}
                >
                  {t('file-upload.select_all')} <b>({items.length})</b>
                </Checkbox>
                {step.importFutureItems && (
                  <Checkbox
                    data-cy="import-future-items"
                    onChange={onImportFutureItems}
                    checked={importFutureItems}
                    disabled={!checkAllItems}
                  >
                    {t('Integration.import_future')} {step.itemDisplayName}
                  </Checkbox>
                )}
              </div>
              <Divider className="wizard__divider m-0 mt-8 mx-auto" />
              <CheckboxGroup
                value={selectedItems}
                onChange={onItemsSelectionChange}
                data-cy="selection-list"
                className="masonry-list w-100% flex-1 min-h-0 pl-5"
              >
                <div
                  className={`masonry-container min-h-0 h-100% overflow-auto ${computedCSSClasses.gridColCSS}`}
                  ref={listContainerRef}
                >
                  {listContainerRef?.current && (
                    <MasonryComponent
                      masonryRef={_masonryRef}
                      partnerId={partnerId}
                      list={filteredList}
                      step={step}
                      setForceUpdate={setForceUpdate}
                      listContainerRef={listContainerRef.current}
                    />
                  )}
                </div>
              </CheckboxGroup>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default observer(GenericWizardStep);
